import type { SupportMessage } from './types'

// A reply the user has written that the service has not confirmed yet. It is
// always the user's own, since a thread is the only place they can compose.
export interface PendingMessage {
  id: string
  ticketPublicId: string
  body: string
  files: File[]
  createdAt: string
  failed: boolean
}

export const createPendingMessage = (
  ticketPublicId: string,
  body: string,
  files: File[],
  createdAt: string,
  id: string,
): PendingMessage => ({
  id,
  ticketPublicId,
  body,
  files,
  createdAt,
  failed: false,
})

export const withoutPendingMessage = (
  pendingMessages: PendingMessage[],
  pendingId: string,
) => pendingMessages.filter((pending) => pending.id !== pendingId)

export const withFailedPendingMessage = (
  pendingMessages: PendingMessage[],
  pendingId: string,
) =>
  pendingMessages.map((pending) =>
    pending.id === pendingId ? { ...pending, failed: true } : pending,
  )

export const withRetriedPendingMessage = (
  pendingMessages: PendingMessage[],
  pendingId: string,
) =>
  pendingMessages.map((pending) =>
    pending.id === pendingId ? { ...pending, failed: false } : pending,
  )

export const pendingMessagesForTicket = (
  pendingMessages: PendingMessage[],
  ticketPublicId: string,
) =>
  pendingMessages.filter((pending) => pending.ticketPublicId === ticketPublicId)

// The service echoes the user's own reply back over the stream, often before
// the POST that created it answers. An echo belongs to a reply that is still in
// flight, so a twin still sending is taken first; a failed twin is only claimed
// when there is no other candidate, or resending the same text would delete the
// red bubble the user still has to deal with.
export const withoutEchoedPendingMessage = (
  pendingMessages: PendingMessage[],
  ticketPublicId: string,
  message: SupportMessage,
) => {
  if (message.authorType !== 'user') return pendingMessages

  const isTwin = (pending: PendingMessage) =>
    pending.ticketPublicId === ticketPublicId && pending.body === message.body

  const sending = pendingMessages.findIndex(
    (pending) => isTwin(pending) && !pending.failed,
  )
  const echoed = sending === -1 ? pendingMessages.findIndex(isTwin) : sending
  if (echoed === -1) return pendingMessages

  return pendingMessages.filter((_pending, position) => position !== echoed)
}
